import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { X, Send, Trash2, ThumbsUp, ThumbsDown, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { lessonPlanApi, reflectiveApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";
import { reflectiveAvailable } from "@/lib/reflectiveAvailable";

// SIG-2: a client-generated per-session id for a Mira response, giving a thumbs rating a stable
// `message_ref`. Mira threads are per-session FE state and the backend returns no message id, so a
// per-session id is sufficient (idempotency is per rendered message). crypto.randomUUID with a
// fallback for environments (older browsers / jsdom) that lack it.
let _miraMsgSeq = 0;
function newMessageId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through to the counter */
  }
  _miraMsgSeq += 1;
  return `mira-msg-${_miraMsgSeq}`;
}
import { useAuth } from "@/hooks/useAuth";
import { usePanelOpen } from "@/components/floatingPanel";
import { onMiraIntent } from "@/features/mira/miraIntentBus";
import { PlanReviewBlock } from "@/features/mira/PlanReviewBlock";
import { BriefBlock } from "@/features/mira/BriefBlock";
import { displayTitle, newestFirst, threadFromTurns, titleFromMessage } from "@/features/mira/savedConversations";

/**
 * R4 / B8-f — Mira, the reflective companion surface, mounted for EVERY role.
 *
 * ROLE-AGNOSTIC MOUNT (B8-f): Mira is a permanent per-user reflective companion for every
 * authenticated role — teacher, school_admin, training_admin, super_admin — each within
 * their own wall-isolated workspace. The role flows to the backend so input_stream_for(role)
 * selects the right stream (admin → the admin's OWN judgment patterns, never teacher
 * adherence; teacher → the teacher's own teaching). There is NO teacher-only clamp.
 *
 * GATING (the flag is the single switch, and the org-pilot gate is the rollout switch):
 * eligibility is (a) REFLECTIVE_SPACE_ENABLED and (b) the viewer's org tagged reflective_pilot
 * — the role-agnostic, org-scoped rollout gate. A failed / loading opening renders NOTHING
 * (silent collapse — zero Mira surface, any role, no broken/empty box).
 *
 * F63 — HOW THAT ELIGIBILITY IS LEARNED CHANGED, AND THIS SURFACE HAD NOT FOLLOWED.
 * This component used to discover eligibility by CALLING GET /reflective/opening and reading
 * the 404. That was the right design when the 404 was the only signal, and the comment here
 * said so: "No separate FE flag plumbing; the query is enabled for any authenticated user and
 * the backend gate decides."
 *
 * REFLECTIVE-FLAG-1 then made that obsolete and nothing here moved. `/api/me` now REPORTS the
 * gate as `reflective_available`, computed from `is_reflective_available` — the SAME predicate
 * the endpoints gate on, deliberately the same function and not a second implementation of the
 * same facts. Its own docstring states the purpose in terms: so the client "can stop calling
 * the four routes that otherwise wait on a 404 just to learn availability"
 * (backend/app/reflective/availability.py:10-11).
 *
 * The client never stopped. So in EVERY non-pilot org, every page load of every school-tier
 * role fired a request whose only purpose was to be refused — a 404 per page, forever, to
 * learn a boolean the client was already holding. That is the defect, and it is not an admin
 * one: it fired for teachers too. The fix is to read the field.
 *
 * IT IS A SNAPSHOT, NOT AN AUTHORIZATION. availability.py says both, and both matter here:
 * eligibility can change between the /api/me read and a later click, so the endpoints keep
 * gating themselves every request and the silent-collapse path below is KEPT — a client that
 * saw true must still survive a 404. This field decides only what we SHOW.
 *
 * DEFAULT OFF WHEN UNKNOWN (`=== true`, not `!== false`). LOGIN-SEAM-1 shows the login payload
 * first and replaces it with the /api/me projection when it arrives, so for a moment after
 * sign-in the field is ABSENT rather than false. Absent must mean "do not call": Mira appears
 * a beat later, which is correct, where defaulting on would restore the 404 precisely in the
 * window LOGIN-SEAM-1 exists to describe.
 *
 * VISUAL CONTRACT (mira_mockup.html): a calm floating launcher (NO pulse), a PARTIAL
 * panel (~396px), a time-aware salutation + soft offer with two entry chips (accept →
 * from_prompt with the seed as turn one / cold open → open), a thread, and a
 * t("miraPanel.tabNoticed") see-and-correct tab. The voice itself is the backend's contract
 * (offer-AND-question, never a verdict) — the FE only renders it.
 */
// R4 addendum — Mira's dedicated mark (the iris / reflective orb from the mockup: a
// radial-gradient circle with a dark pupil and a glint). NOT a generic chat bubble, so
// Mira reads as herself. Unique gradient id avoids collisions if rendered more than once.
function MiraOrb({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        {/* T9 Fix-pack C #2: Mira owns the dedicated Mira token (--cv-mira, #5D55C8 purple) — a
            purple iris with a lighter rim and a warm glint. Calendar/chat launchers stay neutral,
            so the cluster reads "Mira is the purple one". */}
        <radialGradient id="mira-orb-iris" cx="38%" cy="32%" r="72%">
          <stop offset="0%" stopColor="#c9c4f0" />
          <stop offset="45%" stopColor="#5D55C8" />
          <stop offset="100%" stopColor="#2a2568" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="url(#mira-orb-iris)" />
      <circle cx="12" cy="12" r="4.1" fill="#241f5c" opacity="0.9" />
      <circle cx="9.4" cy="9.2" r="1.5" fill="#fffdf8" opacity="0.95" />
    </svg>
  );
}

export function MiraLauncher() {
  const { user } = useAuth();
  // F63 / C32: the role fallback (tenant_role || role), the master-admin (super_admin) exclusion
  // and the reflective_available === true rollout gate now live in ONE predicate
  // (lib/reflectiveAvailable), shared with the teacher workspace's docked-rail opening query so
  // neither call site fires GET /reflective/opening — and 404s — in a non-Mira org.
  const miraCallable = reflectiveAvailable(user);

  const openingQuery = useQuery({
    queryKey: ["mira-opening"],
    queryFn: () => reflectiveApi.opening().then((res) => res.data),
    // B8-f: enabled for any authenticated SCHOOL-TIER role (role-agnostic mount within a
    // workspace) — teacher, school_admin and training_admin alike, which is deliberate and
    // unchanged: the backend builds an ADMIN opening turn for exactly this (MIRA_ADMIN_ROLES /
    // MIRA_ADMIN_OPENING_TURN, routes.py:702-706) and serves admins their own reflective reads.
    // MA-2: master-admin scope is excluded entirely.
    // F63: and only where the org HAS Mira. Previously this fired for every school-tier role in
    // every org and let a 404 answer the question — see the F63 note in the header.
    enabled: miraCallable,
    retry: false,
  });

  // Master-admin scope never renders Mira (MA-2 rider). Unauthenticated / opening 404s
  // (flag-off OR non-pilot org) / still loading -> no surface at all (silent collapse).
  // F63: `!reflectiveAvailable` is stated here too, and not left to follow from the disabled
  // query having no data. The render decision should not depend on reading off what a disabled
  // TanStack v5 query reports for isLoading/isError; it should say what it means. The 404 path
  // is KEPT beside it, because availability is a snapshot and the server still refuses.
  if (
    !miraCallable ||
    openingQuery.isLoading ||
    openingQuery.isError ||
    !openingQuery.data
  ) {
    return null;
  }

  return <MiraSurface opening={openingQuery.data} />;
}

// W64 — what the in-Mira upload accepts: My Library's own set (PDF, DOCX, or a photo). The server's
// allowlist is the authority; this only shapes the picker, as DocumentCreateForm's does.
export const MIRA_UPLOAD_ACCEPT = ".pdf,.docx,.png,.jpg,.jpeg,.webp,.heic";

function MiraSurface({ opening }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // W55 — saved conversations are a TEACHER surface. An admin's Mira is unchanged: no history tab,
  // nothing saved. The server fences every read on the owner regardless; this decides what we show.
  // W64 — a teacher can also upload a document from inside Mira. It is filed in HER My Library through
  // the SAME `lessonPlanApi.upload` My Library uses (same allowlist, size cap, EXIF strip,
  // extraction), and every later turn names it, so the server carries its extracted text into this
  // conversation.
  const { user } = useAuth();
  const keepsConversations = (user?.tenant_role || user?.role) === "teacher";
  // ⚑ F-11 (C-371) — WHO IS READING THIS PANEL. An administrator opening Mira was reading the
  // TEACHER's entry label and the teacher's cold-open prompt: `reflectiveAvailable` admits admins
  // (it excludes only `super_admin`), so the panel opened for them and then addressed the wrong
  // person. LANE-1 shipped `miraAdmin.*` for exactly this; nothing was wired to it.
  //
  // Derived from the same role fallback this file already uses two lines up, rather than from a
  // second source — `super_admin` never reaches here because `reflectiveAvailable` has already
  // excluded them, and a check for it here would be a second gate drifting from the first.
  const miraReaderRole = user?.tenant_role || user?.role;
  const readsAsAdmin = miraReaderRole === "school_admin" || miraReaderRole === "training_admin";
  const uploadTeacherId =
    (user?.tenant_role || user?.role) === "teacher" ? user?.teacher_id || null : null;
  const [conversationId, setConversationId] = useState(null); // the saved conversation in the thread
  const [attachedDocIds, setAttachedDocIds] = useState([]);
  const fileInputRef = useRef(null);
  // TEACH-UX P1-a — provider-owned open state. Mira is now the ONE floating button, so the old
  // "hide my button when another panel is open" dance is gone; mutual exclusion is structural,
  // because the provider holds a single panel name. Standalone Mira still self-drives.
  const [open, setOpen] = usePanelOpen("mira");
  // MON-1 (b): on the teacher Home (/my-workspace), the docked Mira RAIL is the single Mira entry —
  // suppress the floating launcher BUTTON there (the panel still opens via the rail's actions). The
  // bubble remains on every other page.
  const { pathname } = useLocation();
  const suppressLauncherOnHome = pathname === "/my-workspace" || pathname.startsWith("/my-workspace/");
  const [tab, setTab] = useState("chat"); // "chat" | "conversations" | "documents" | "noticed"
  const [hasNewDoc, setHasNewDoc] = useState(false); // MIRA-2: "new" dot on the Documents tab
  const [activeDocId, setActiveDocId] = useState(null); // Cluster B: the doc open in the reader
  const [mode, setMode] = useState(null); // null until the teacher picks an entry mode
  const [seed, setSeed] = useState("");
  const [thread, setThread] = useState([]); // [{ role: "user" | "mira", text, id? }]
  const [input, setInput] = useState("");
  // MON-2 auto-send: a rail/starter intent can carry { autoSend, prompt, nonce } to open Mira AND
  // submit the message in one gesture (no retype). `pendingAutoSend` holds the carried turn until
  // the panel is open and idle; `sentNonceRef` records the last nonce actually sent, so the effect
  // is idempotent under re-render / StrictMode double-invoke — exactly one send per gesture.
  const [pendingAutoSend, setPendingAutoSend] = useState(null); // { text, nonce } | null
  const sentNonceRef = useRef(null);
  const [ratings, setRatings] = useState({}); // SIG-2: { [messageId]: "up" | "down" }
  // PLAN-2 M4 — an intent (from the general bus) can open Mira addressed to a
  // task. plan_review opens the panel focused on one plan.
  const [activeReview, setActiveReview] = useState(null); // { planId, title } | null
  const [activeBrief, setActiveBrief] = useState(null); // { teacherId, teacherName } | null

  useEffect(
    () =>
      onMiraIntent((intent) => {
        if (intent?.type === "plan_review" && intent.planId) {
          setOpen(true);
          setTab("chat");
          setActiveBrief(null);
          setActiveReview({ planId: intent.planId, title: intent.title });
        } else if (intent?.type === "pre_conference_brief" && intent.teacherId) {
          setOpen(true);
          setTab("chat");
          setActiveReview(null);
          setActiveBrief({ teacherId: intent.teacherId, teacherName: intent.teacherName });
        } else if (intent?.type === "goal" && intent.goalId) {
          // GOALS-1 — open Mira "about this goal". The teacher's active goals are ALREADY in Mira's
          // grounding, so a prefilled prompt suffices this phase; GOALS-3 upgrades this to a dedicated
          // goal-focus block + server endpoint (the plan_review precedent).
          setOpen(true);
          setTab("chat");
          setActiveReview(null);
          setActiveBrief(null);
          if (intent.prompt) setInput(intent.prompt);
        } else if (intent?.type === "first_run") {
          // UX-6c F3 — THE MIRA DOOR. The first-run checklist opens Mira with a
          // ready-to-send prompt so a brand-new user can just press send.
          setOpen(true);
          setTab("chat");
          setActiveReview(null);
          setActiveBrief(null);
          if (intent.autoSend && intent.prompt) {
            // MON-2: the Home rail already captured the teacher's message — carry it straight into a
            // real turn (submitted on open via the existing send path), not a prefill they must re-send.
            setPendingAutoSend({ text: intent.prompt, nonce: intent.nonce });
          } else if (intent.prompt) {
            setInput(intent.prompt);
          }
        }
      }),
    []
  );

  const saveBriefAsDocument = (brief) => {
    const line = (points) => (points || []).map((p) => `• ${p.text}`);
    const body = [
      brief.basis_line,
      "",
      t("miraPanel.whereThingsStand"),
      ...line(brief.where_things_stand),
      "",
      t("miraPanel.whatsOpen"),
      ...line(brief.whats_open),
      "",
      t("miraPanel.talkingPoints"),
      ...line(brief.talking_points),
    ]
      .filter((l) => l !== undefined)
      .join("\n");
    setThread((prev) => [
      ...prev,
      {
        role: "proposal",
        proposal: {
          action: "generate_doc",
          payload: { title: t("miraPanel.briefDocTitle", { name: activeBrief?.teacherName || "" }), body },
          summary: t("miraPanel.saveBriefAsDoc"),
        },
        status: "pending",
      },
    ]);
  };

  // M4c — "save as document" reuses the EXISTING generate_doc propose->confirm.
  const saveReviewAsDocument = (review) => {
    const body = [
      review.basis_line,
      review.no_anchor_disclosure,
      ...(review.suggestions || []).map((s) => `• ${s.text}`),
    ]
      .filter(Boolean)
      .join("\n");
    setThread((prev) => [
      ...prev,
      {
        role: "proposal",
        proposal: {
          action: "generate_doc",
          payload: { title: t("miraPanel.reviewDocTitle", { title: activeReview?.title || "" }), body },
          summary: t("miraPanel.saveReviewAsDoc"),
        },
        status: "pending",
      },
    ]);
  };

  const greeting = opening.greeting_context || {};
  // HEB-1: greeting is composed per-locale. The time-of-day is a translated token (in he it carries
  // the FULL greeting, e.g. "בוקר טוב", and the salutation template is bare); en keeps "Good {{x}}".
  const timeKey = { morning: "miraPanel.timeMorning", afternoon: "miraPanel.timeAfternoon", evening: "miraPanel.timeEvening" }[greeting.time_of_day];
  const timeOfDay = timeKey ? t(timeKey) : (greeting.time_of_day || "");
  const salutation = greeting.name
    ? t("miraPanel.salutationNamed", { timeOfDay, name: greeting.name })
    : t("miraPanel.salutation", { timeOfDay });

  const invalidateConversations = () => queryClient.invalidateQueries({ queryKey: ["mira-conversations"] });

  const invalidateSuggestions = () => {
    queryClient.invalidateQueries({ queryKey: ["mira-suggestions"] });
    queryClient.invalidateQueries({ queryKey: ["mira-suggestions-count"] });
  };

  // MIRA-2: streaming send. The reply text arrives incrementally over SSE (~2-4s to first words
  // vs 30-54s to a whole reply); the proposal — when present — rides the TERMINAL event and renders
  // in the SAME inert confirm card as before, so the propose→confirm→execute→SIG-2 flow is
  // unchanged. `streaming` gates the composer; `awaitingFirstToken` shows the "thinking" indicator
  // ONLY until the first text delta lands.
  const [streaming, setStreaming] = useState(false);
  const [awaitingFirstToken, setAwaitingFirstToken] = useState(false);
  const streamAbortRef = useRef(null);
  const streamMsgIdRef = useRef(null);
  const messagesEndRef = useRef(null);

  // MIRA-2: keep the newest text in view as it streams in. Guarded — jsdom lacks scrollIntoView.
  useEffect(() => {
    const el = messagesEndRef.current;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "end" });
  }, [thread]);

  const patchMessage = (id, patch) =>
    setThread((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m)));

  const runStream = (payload) => {
    // A stable LOCAL id keys the placeholder for React + in-place delta patching; the server's
    // message_ref (from event:start) is stored separately on `ref` and is what thumbs bind to.
    const localId = newMessageId();
    streamMsgIdRef.current = localId;
    setThread((prev) => [...prev, { role: "mira", text: "", id: localId, streaming: true, retryPayload: payload }]);
    setStreaming(true);
    setAwaitingFirstToken(true);
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const clearFlags = () => { setStreaming(false); setAwaitingFirstToken(false); streamAbortRef.current = null; streamMsgIdRef.current = null; };

    reflectiveApi.respondStream(payload, {
      signal: controller.signal,
      onStart: (ref) => { if (ref) patchMessage(localId, { ref }); },
      onDelta: (chunk) => {
        setAwaitingFirstToken(false);
        patchMessage(localId, (m) => ({ text: (m.text || "") + chunk }));
      },
      onDone: (reply) => {
        const proposal = reply.proposal;
        const isSnooze = proposal && proposal.action === "snooze_suggestion";
        if (isSnooze) {
          // TRUST INVARIANT: a snooze turn shows ONLY the hold-off acknowledgment (never the lead
          // AND a card) — so we REPLACE any streamed lead with the hold-off line, then execute.
          patchMessage(localId, { text: t("miraPanel.holdOff"), streaming: false });
          reflectiveApi.executeAction({ action: proposal.action, payload: proposal.payload })
            .then(invalidateSuggestions)
            .catch(() => {});
          return;
        }
        // ⚑ F-10 (C-371) — A BACKEND FALLBACK IS A FAILURE, AND IT IS NOW RENDERED AS ONE.
        //
        // The server frames a config-level or empty-reply fallback as a terminal `done` carrying
        // `fallback: true` (causes: sdk_missing, key_missing, empty_reply, api_error). This branch
        // did not exist, so such a turn took the ordinary path below with `error: false` — which
        // had two consequences, both of them reported in the walk as separate mysteries:
        //
        //   * the reply text is the server's `_FALLBACK_TEXT`, which is ENGLISH prose. A Hebrew
        //     reader was shown English, in a product whose Hebrew is a correctness rule (R3).
        //   * with `error: false` there is no retry affordance, so a failed turn looked like an
        //     ANSWER. A teacher reads that as Mira changing the subject, not as a fault.
        //
        // ⚑ AND IT IS WHY THE WALK'S TWO SIGHTINGS WERE THE SAME BUG WEARING TWO FACES: the
        // LANGUAGE of the text told you which path you were on. English prose = this branch
        // (backend fallback). Hebrew = `onError` below. Nobody had a reason to connect them.
        //
        // The localized error text is used in place of the server's prose deliberately: it is
        // honest in both languages and carries the retry. The in-character English line is a
        // nicety we give up here rather than ship English to a Hebrew reader; if the owner wants
        // it back, the server receives `language` already and should return it localized — which
        // is a copy row for LANE-1, not a branch in this component.
        if (reply.fallback) {
          patchMessage(localId, {
            text: t("miraPanel.streamError"),
            streaming: false,
            error: true,
            errorCause: reply.fallback_cause || "fallback",
          });
          return;
        }
        // The terminal reply is authoritative over the concatenated deltas (matches /respond).
        patchMessage(localId, { text: reply.text || "", streaming: false, error: false });
        // W55: the server appended this exchange to the saved conversation; its place in the list moved.
        if (payload.conversation_id) invalidateConversations();
        // P7b: an ACTION proposal is INERT — a separate confirmation card; nothing runs until Confirm.
        if (proposal) setThread((prev) => [...prev, { role: "proposal", proposal, status: "pending" }]);
      },
      // ⚑ F-10 (C-371) — THE CAUSE IS KEPT INSTEAD OF DISCARDED, and that is the whole of why
      // F-10 could not be diagnosed from a browser.
      //
      // This handler was declared `() => {}`, taking NO ARGUMENT. `respondStream` distinguishes
      // four failure modes and passes the cause for every one of them — `network_error` (the
      // fetch never completed), `http_error` WITH the status (a non-2xx, or a 200 whose body is
      // not a readable stream), `stream_error` (the read or the SSE parse threw), and a
      // server-sent mid-stream `error` frame. All four arrived here and were thrown away, so
      // every one of them produced the same single sentence on screen and nothing anywhere else.
      //
      // ⚑ The product was computing the answer to "why did Mira fail?" and then dropping it on
      // the floor. The console line is the one place a tester can read it with no backend access,
      // and `errorCause` keeps it on the bubble so a later surface can tell the modes apart
      // without re-plumbing this seam.
      onError: (info) => {
        const cause = (info && info.fallback_cause) || "stream_error";
        // eslint-disable-next-line no-console
        console.error("MIRA_STREAM_ERROR", { cause, status: info && info.status });
        // Keep whatever partial text already streamed; mark the bubble errored + offer a retry.
        patchMessage(localId, (m) => ({
          streaming: false,
          error: true,
          errorCause: cause,
          text: m.text || t("miraPanel.streamError"),
        }));
      },
    }).finally(clearFlags);
  };

  const stopStreaming = () => {
    if (streamAbortRef.current) streamAbortRef.current.abort();
    if (streamMsgIdRef.current) patchMessage(streamMsgIdRef.current, { streaming: false });
    setStreaming(false);
    setAwaitingFirstToken(false);
  };

  const retryStream = (payload) => { if (!streaming && payload) runStream(payload); };

  const resolveProposal = (index, patch) =>
    setThread((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  // SIG-2: thumbs on a Mira response. Local selected-state is optimistic; the signal write is
  // fire-and-forget (never block or toast on a capture failure). Latest click wins (up<->down).
  const rate = (messageId, rating) => {
    setRatings((prev) => ({ ...prev, [messageId]: rating }));
    reflectiveApi.rateMessage({ message_ref: messageId, rating }).catch(() => {});
  };

  // P7b-Tier 1 — proactive suggestions: the badge count (polled) + the on-open list.
  const suggestionsCountQuery = useQuery({
    queryKey: ["mira-suggestions-count"],
    queryFn: () => reflectiveApi.suggestionsCount().then((res) => res.data),
    refetchInterval: 45000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const suggestionCount = suggestionsCountQuery.data?.count || 0;

  const suggestionsQuery = useQuery({
    queryKey: ["mira-suggestions"],
    queryFn: () => reflectiveApi.suggestions().then((res) => res.data),
    enabled: open,
    retry: false,
  });
  const [handledKeys, setHandledKeys] = useState([]); // suggestions accepted/snoozed this session
  // PLAN-R2: TYPED nudges (plan_recording) are actioned on the Home rail's SuggestionCard, not the
  // launcher's generic Accept→P7b-confirm path (which can't execute a plan_recording action). Drop
  // them here so the panel never shows a broken Accept; the badge count excludes them symmetrically.
  const suggestions = (suggestionsQuery.data?.suggestions || [])
    .filter((s) => (s.proposed_action?.action || "") !== "plan_recording")
    .filter((s) => !handledKeys.includes(s.suggestion_key));

  const snoozeMutation = useMutation({
    mutationFn: ({ key, until }) =>
      (until === null ? reflectiveApi.dismissSuggestion(key) : reflectiveApi.snoozeSuggestion(key, until)).then((res) => res.data),
    onSuccess: (_data, vars) => { setHandledKeys((prev) => [...prev, vars.key]); invalidateSuggestions(); },
  });

  const acceptSuggestion = (s) => {
    // Accept -> the suggestion's ready P7b proposal renders in the PROVEN confirm card (unchanged).
    setTab("chat");
    // SIG-2: carry the suggestion_key onto the proposal message so an Accept→execute can link the
    // action back to the suggestion that spawned it (suggestion_acted).
    setThread((prev) => [...prev, { role: "proposal", proposal: { ...s.proposed_action, summary: s.title }, suggestion_key: s.suggestion_key, status: "pending" }]);
    setHandledKeys((prev) => [...prev, s.suggestion_key]);
  };

  const nextWeekIso = () => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString(); };

  const startFromPrompt = () => {
    // Accept the offer: the seed (opening_turn) becomes Mira's first message; the next
    // user send is a from_prompt turn carrying the seed as turn one.
    setMode("from_prompt");
    setSeed(opening.opening_turn || "");
    setThread(opening.opening_turn ? [{ role: "mira", text: opening.opening_turn, id: newMessageId() }] : []);
  };

  const startColdOpen = () => {
    setMode("open");
    setSeed("");
    setThread([]);
  };

  // W55 — the saved conversation this send belongs to. The FIRST message of a new conversation saves
  // it (titled with her own words, carrying Mira's offer line if she opened from it) BEFORE the turn
  // streams, so the turn can name it: the server then replays THIS conversation's turns rather than
  // whatever she said last, and appends the exchange itself. A failed save never blocks Mira — the
  // turn goes out unsaved, exactly as it did before this existed.
  const ensureConversation = (text, priorThread) => {
    if (!keepsConversations) return Promise.resolve(null);
    if (conversationId) return Promise.resolve(conversationId);
    const seedTurns = priorThread
      .filter((m) => m.role === "mira" && m.text && !m.error)
      .map((m) => ({ role: "mira", text: m.text }));
    return Promise.resolve()
      .then(() => reflectiveApi.saveConversation({ title: titleFromMessage(text), turns: seedTurns }))
      .then((res) => {
        const id = res?.data?.conversation?.id || null;
        if (id) {
          setConversationId(id);
          invalidateConversations();
        }
        return id;
      })
      .catch(() => null);
  };

  // The ONE send path — the composer's Send, and MON-2's auto-send handoff, both route through it.
  const submitText = (raw) => {
    const text = (raw || "").trim();
    if (!text || streaming) return;
    const activeMode = mode || "open";
    const priorThread = thread;
    setThread((prev) => [...prev, { role: "user", text }]);
    setInput("");
    const payload = { text, seed: activeMode === "from_prompt" ? seed : null, mode: activeMode };
    // The seed is turn-one only; continuation is an open thread.
    setMode("open");
    setSeed("");
    // Hold the composer through the save, so a second Enter cannot send twice before the stream opens.
    setStreaming(true);
    setAwaitingFirstToken(true);
    // ⚑ MERGE (W55 x W64): the attached document ids go on the payload HERE, not at the send site.
    // W64 called runStream immediately; W55 moved that call inside this `.then` so the save can name
    // the conversation first. Keeping BOTH lines — which is what taking either side of the conflict
    // whole would have done — sends TWO turns for one press.
    ensureConversation(text, priorThread).then((id) => {
      const withDocs = attachedDocIds.length
        ? { ...payload, document_ids: attachedDocIds }
        : payload;
      runStream(id ? { ...withDocs, conversation_id: id } : withDocs);
    });
  };

  // W55 — start a new one: an empty thread that belongs to no saved conversation yet.
  const startNewConversation = () => {
    if (streaming) return;
    setConversationId(null);
    setThread([]);
    setMode(null);
    setSeed("");
    setInput("");
    setActiveReview(null);
    setActiveBrief(null);
    setTab("chat");
  };

  // W55 — reopen one and continue it. Its turns become the thread; the next send names it.
  const [reopenFailed, setReopenFailed] = useState(false);
  const reopenConversation = (id) => {
    if (streaming) return;
    setReopenFailed(false);
    reflectiveApi
      .conversation(id)
      .then((res) => {
        const row = res?.data?.conversation;
        if (!row) throw new Error("missing");
        setConversationId(row.id);
        setThread(threadFromTurns(row.turns, newMessageId));
        setMode("open");
        setSeed("");
        setInput("");
        setActiveReview(null);
        setActiveBrief(null);
        setTab("chat");
      })
      .catch(() => {
        setReopenFailed(true);
        invalidateConversations();
      });
  };

  // W55 — a deleted conversation is gone from the list at once; if it was the one open, the thread
  // it was in goes with it, so nothing on screen still belongs to a conversation that no longer exists.
  const onConversationDeleted = (id) => {
    if (id === conversationId) {
      setConversationId(null);
      setThread([]);
      setMode(null);
      setSeed("");
    }
  };
  const send = () => submitText(input);

  // MON-2 auto-send: once the panel is open and idle, submit the carried message ONCE. The nonce
  // guard (not just clearing state) makes this safe against effect re-invocation — a re-render or
  // StrictMode's double-mount re-runs this with the same pending turn, but a matching nonce is a
  // no-op, so Mira never receives a duplicate message.
  useEffect(() => {
    if (!pendingAutoSend || !open || streaming) return;
    if (sentNonceRef.current === pendingAutoSend.nonce) return;
    sentNonceRef.current = pendingAutoSend.nonce;
    submitText(pendingAutoSend.text);
    setPendingAutoSend(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoSend, open, streaming]);

  // W64 — the upload itself. The note it leaves in the thread never names the file: a filename is
  // not copy (CLAUDE.md), and the document's place in My Library is what she needs to know.
  const uploadMutation = useMutation({
    mutationFn: (file) => {
      const data = new FormData();
      data.append("file", file);
      data.append("teacher_id", uploadTeacherId);
      data.append("title", "");
      data.append("kind", "lesson_plan");
      return lessonPlanApi.upload(data).then((res) => res.data);
    },
    onMutate: () => {
      const id = newMessageId();
      setThread((prev) => [...prev, { role: "upload", id, status: "uploading" }]);
      return { id };
    },
    onSuccess: (created, _file, ctx) => {
      if (created?.id) setAttachedDocIds((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
      patchMessage(ctx.id, { status: "done" });
      queryClient.invalidateQueries({ queryKey: ["teacher-plans", uploadTeacherId] });
      queryClient.invalidateQueries({ queryKey: ["lesson-plans", uploadTeacherId] });
    },
    onError: (error, _file, ctx) => {
      patchMessage(ctx.id, { status: "error", errorText: apiErrorMessage(error, t("planning.toastUploadFailed")) });
    },
  });
  const onPickFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file && uploadTeacherId) uploadMutation.mutate(file);
  };

  const hasOffer = Boolean(opening.offer_line);

  return (
    <>
      {/* Floating launcher — calm, no pulse. A pure OPENER now (UI-10 0a): it hides while the panel
          is open (the panel owns its own × close), while a DIFFERENT panel is open (no overlap), and
          on Home where the docked rail is Mira's single entry (MON-1). One close control per open
          panel, everywhere. */}
      {suppressLauncherOnHome || open ? null : (
        /* F-11: an administrator is offered the school, not her own practice. */
        <button
          type="button"
          data-testid="mira-launcher"
          aria-label={readsAsAdmin ? t("miraAdmin.entryLabel") : t("miraPanel.launcherAria")}
          onClick={() => setOpen(true)}
          className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] end-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-lg ring-1 ring-slate-200 transition hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 md:bottom-5"
        >
          <MiraOrb className="h-9 w-9" />
          {/* P7b-Tier 1 — the badge: un-snoozed suggestions Mira noticed. Prevents the thing being
              forgotten even if the user doesn't open Mira. */}
          {!open && suggestionCount > 0 ? (
            <span
              data-testid="mira-badge"
              className="absolute -end-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-white shadow"
            >
              {suggestionCount}
            </span>
          ) : null}
        </button>
      )}

      {open ? (
        <div
          data-testid="mira-panel"
          /* R4 addendum: enlarged toward a LegalZoom-like chat surface (was 396×70vh).
             Still partial-screen / bottom-right; viewport caps keep it from overflowing
             on small screens. */
          className="fixed bottom-[calc(7rem+env(safe-area-inset-bottom))] end-5 z-40 flex h-[660px] max-h-[calc(100vh-8rem-env(safe-area-inset-bottom))] w-[440px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl md:bottom-24 md:max-h-[calc(100vh-7rem)]"
        >
          {/* Header + tabs */}
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <MiraOrb className="h-5 w-5" />
              {/* i18n-exempt: brand name */}
              <span className="font-display text-base font-semibold text-ink">Mira</span>
            </div>
            <div className="flex gap-1 text-xs font-medium">
              <button
                type="button"
                data-testid="mira-tab-chat"
                onClick={() => setTab("chat")}
                className={`rounded-md px-2 py-1 ${tab === "chat" ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-slate-700"}`}
              >{t("miraPanel.tabChat")}</button>
              {keepsConversations ? (
                <button
                  type="button"
                  data-testid="mira-tab-conversations"
                  onClick={() => { setReopenFailed(false); setTab("conversations"); }}
                  className={`rounded-md px-2 py-1 ${tab === "conversations" ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-slate-700"}`}
                >{t("miraPanel.tabConversations")}</button>
              ) : null}
              <button
                type="button"
                data-testid="mira-tab-documents"
                onClick={() => { setActiveDocId(null); setTab("documents"); setHasNewDoc(false); }}
                className={`relative rounded-md px-2 py-1 ${tab === "documents" ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-slate-700"}`}
              >
                {t("miraPanel.tabDocuments")}
                {hasNewDoc ? (
                  <span data-testid="mira-documents-new-dot" aria-label={t("miraPanel.newDocDot")} className="absolute -end-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />
                ) : null}
              </button>
              <button
                type="button"
                data-testid="mira-tab-noticed"
                onClick={() => setTab("noticed")}
                className={`rounded-md px-2 py-1 ${tab === "noticed" ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-slate-700"}`}
              >{t("miraPanel.tabNoticed")}</button>
              {/* UI-10 0a — the panel's OWN close, always rendered in its header, source-agnostic
                  (works whether the panel was opened from the bubble, the Home rail, or an intent).
                  Wired to the same setOpen the launcher uses, so it flows to the shared open-state. */}
              <button
                type="button"
                data-testid="mira-panel-close"
                aria-label={t("miraPanel.close")}
                onClick={() => setOpen(false)}
                className="ms-1 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          {tab === "chat" ? (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                {/* PLAN-2 M4c — a "review my plan" intent renders here, scope-honesty leading. */}
                {activeReview ? (
                  <div data-testid="mira-plan-review" className="rounded-xl border border-gold-soft bg-ivory-2 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("miraPanel.planReviewLabel")}</p>
                      <button
                        type="button"
                        data-testid="mira-plan-review-dismiss"
                        onClick={() => setActiveReview(null)}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700"
                      >{t("miraPanel.dismiss")}</button>
                    </div>
                    <PlanReviewBlock
                      planId={activeReview.planId}
                      planTitle={activeReview.title}
                      onSaveAsDocument={saveReviewAsDocument}
                    />
                  </div>
                ) : null}
                {/* PLAN-2 M5 — a "brief me" intent renders the pre-conference brief here. */}
                {activeBrief ? (
                  <div data-testid="mira-brief" className="rounded-xl border border-gold-soft bg-ivory-2 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("miraPanel.briefLabel")}</p>
                      <button
                        type="button"
                        data-testid="mira-brief-dismiss"
                        onClick={() => setActiveBrief(null)}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700"
                      >{t("miraPanel.dismiss")}</button>
                    </div>
                    <BriefBlock
                      teacherId={activeBrief.teacherId}
                      teacherName={activeBrief.teacherName}
                      onSaveAsDocument={saveBriefAsDocument}
                    />
                  </div>
                ) : null}
                {/* P7b-Tier 1 — proactive suggestions Mira noticed, on open. Accept flows into the
                    proven P7b confirm card; snooze/dismiss tune relevance (self-scoped, direct). */}
                {suggestions.length ? (
                  <div data-testid="mira-suggestions" className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("miraPanel.suggestionsHeading")}</p>
                    {suggestions.map((s) => (
                      <div key={s.suggestion_key} data-testid="mira-suggestion" data-signal={s.signal_type} className="rounded-xl border border-gold-soft bg-ivory-2 p-3">
                        <p className="font-semibold text-slate-900">{s.title}</p>
                        {s.body ? <p className="mt-0.5 text-xs leading-5 text-slate-600">{s.body}</p> : null}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <button type="button" data-testid="mira-suggestion-accept" onClick={() => acceptSuggestion(s)} className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90">{t("miraPanel.suggestionDo")}</button>
                          <button type="button" data-testid="mira-suggestion-snooze" onClick={() => snoozeMutation.mutate({ key: s.suggestion_key, until: nextWeekIso() })} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-white">{t("miraPanel.suggestionSnooze")}</button>
                          <button type="button" data-testid="mira-suggestion-dismiss" onClick={() => snoozeMutation.mutate({ key: s.suggestion_key, until: null })} className="rounded-full px-3 py-1 text-xs font-medium text-slate-500 hover:text-slate-700">{t("miraPanel.suggestionDismiss")}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {/* Opening salutation + soft offer (only before the thread starts). */}
                {thread.length === 0 ? (
                  <div data-testid="mira-greeting" className="space-y-3">
                    <p className="font-display text-lg text-ink">{salutation}.</p>
                    {hasOffer ? (
                      <>
                        <p className="text-sm leading-6 text-slate-600">{opening.offer_line}</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            data-testid="mira-offer-accept"
                            onClick={startFromPrompt}
                            className="rounded-full bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
                          >{t("miraPanel.offerAccept")}</button>
                          <button
                            type="button"
                            data-testid="mira-offer-cold"
                            onClick={startColdOpen}
                            className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >{t("miraPanel.offerCold")}</button>
                        </div>
                      </>
                    ) : readsAsAdmin ? (
                      /* ⚑ F-11: the admin cold open. `coldOpenPrompt` is written for a teacher
                         reflecting on her own lesson, and it is the wrong invitation for someone
                         looking at a school. This pair also STATES THE WALL in the one place the
                         question gets asked -- Mira works from what teachers chose to share -- so
                         an administrator learns the boundary before being surprised by it. */
                      <div data-testid="mira-admin-empty" className="space-y-1">
                        <p className="text-sm font-medium text-ink">{t("miraAdmin.emptyTitle")}</p>
                        <p className="text-sm leading-6 text-slate-600">{t("miraAdmin.emptyBody")}</p>
                      </div>
                    ) : (
                      <p className="text-sm leading-6 text-slate-600">{t("miraPanel.coldOpenPrompt")}</p>
                    )}
                  </div>
                ) : (
                  thread.map((message, index) =>
                    message.role === "upload" ? (
                      <MiraUploadNote key={message.id} message={message} />
                    ) : message.role === "proposal" ? (
                      <MiraProposalCard
                        key={index}
                        message={message}
                        onResolve={(patch) => resolveProposal(index, patch)}
                        onOpenDocument={(id) => { setActiveDocId(id); setTab("documents"); setHasNewDoc(false); }}
                        onDocumentCreated={() => setHasNewDoc(true)}
                      />
                    ) : (
                      <div
                        key={message.id || index}
                        data-testid="mira-message"
                        data-role={message.role}
                        className={message.role === "mira" ? "max-w-[85%]" : "ms-auto max-w-[85%]"}
                      >
                        <div
                          className={
                            message.role === "mira"
                              ? "rounded-2xl bg-slate-100 px-3 py-2 font-serif text-sm leading-6 text-slate-800"
                              : "rounded-2xl bg-primary px-3 py-2 text-sm leading-6 text-white"
                          }
                        >
                          {message.text}
                          {/* MIRA-2: a quiet cursor while this bubble is still streaming. */}
                          {message.streaming ? <span data-testid="mira-streaming-cursor" className="ms-0.5 inline-block animate-pulse">▌</span> : null}
                        </div>
                        {/* MIRA-2: honest error state + retry (partial text is kept, not discarded). */}
                        {message.role === "mira" && message.error ? (
                          <div className="mt-1 flex items-center gap-2 text-xs text-amber-600">
                            <span>{t("miraPanel.streamErrorNote")}</span>
                            <button
                              type="button"
                              data-testid="mira-retry"
                              onClick={() => retryStream(message.retryPayload)}
                              className="underline hover:text-amber-700"
                            >{t("miraPanel.retry")}</button>
                          </div>
                        ) : null}
                        {/* SIG-2: unobtrusive thumbs on Mira's own COMPLETED responses. Bind to the
                            server message_ref (event:start) when present, else the local id. */}
                        {message.role === "mira" && message.id && !message.streaming && !message.error ? (
                          <MiraResponseRating
                            messageId={message.ref || message.id}
                            value={ratings[message.ref || message.id]}
                            onRate={rate}
                          />
                        ) : null}
                      </div>
                    )
                  )
                )}
                {awaitingFirstToken ? <p className="text-xs text-slate-400">{t("miraPanel.thinking")}</p> : null}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              <div className="flex items-end gap-2 border-t border-slate-200 px-3 py-2">
                {uploadTeacherId ? (
                  <>
                    <button
                      type="button"
                      data-testid="mira-upload"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      disabled={uploadMutation.isPending}
                      aria-label={t("planning.chooseFile")}
                      title={t("planning.chooseFile")}
                      className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50"
                    >
                      <Paperclip className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={MIRA_UPLOAD_ACCEPT}
                      className="hidden"
                      data-testid="mira-upload-input"
                      onChange={onPickFile}
                    />
                  </>
                ) : null}
                <textarea
                  data-testid="mira-input"
                  rows={1}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  // UI-10 0b — Enter sends, Shift+Enter newlines. The IME guard (isComposing) means
                  // Hebrew/CJK composition NEVER sends mid-word. While streaming, send() is a no-op
                  // (no queue pattern exists), so a stray Enter can't double-fire.
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      send();
                    }
                  }}
                  placeholder={t("miraPanel.composerPlaceholder")}
                  className="max-h-32 flex-1 resize-none rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                />
                {streaming ? (
                  <button
                    type="button"
                    data-testid="mira-stop"
                    onClick={stopStreaming}
                    className="flex h-9 flex-none items-center justify-center rounded-md border border-slate-300 px-3 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    aria-label={t("miraPanel.stopAria")}
                  >{t("miraPanel.stop")}</button>
                ) : null}
                <button
                  type="button"
                  data-testid="mira-send"
                  onClick={send}
                  disabled={streaming}
                  className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
                  aria-label={t("miraPanel.sendAria")}
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : tab === "conversations" && keepsConversations ? (
            <MiraConversations
              activeId={conversationId}
              busy={streaming}
              reopenFailed={reopenFailed}
              onOpen={reopenConversation}
              onNew={startNewConversation}
              onDeleted={onConversationDeleted}
            />
          ) : tab === "documents" ? (
            <MiraDocuments activeDocId={activeDocId} setActiveDocId={setActiveDocId} />
          ) : (
            <MiraNoticed queryClient={queryClient} />
          )}
        </div>
      ) : null}
    </>
  );
}

// W55 — her past conversations, newest first, each with its title and date. Open one to continue
// it; start a new one; delete one (gone from the list at once, and from Mira's reach on the server).
// No expiry wording anywhere: conversations are kept until the account is deleted.
function MiraConversations({ activeId, busy, reopenFailed, onOpen, onNew, onDeleted }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const listQuery = useQuery({
    queryKey: ["mira-conversations"],
    queryFn: () => reflectiveApi.conversations().then((res) => res.data),
    retry: false,
  });
  const rows = newestFirst(listQuery.data?.conversations);

  const deleteMutation = useMutation({
    mutationFn: (id) => reflectiveApi.deleteConversation(id).then((res) => res.data),
    onSuccess: (_data, id) => {
      queryClient.setQueryData(["mira-conversations"], (prev) =>
        prev ? { ...prev, conversations: (prev.conversations || []).filter((r) => r.id !== id) } : prev
      );
      queryClient.invalidateQueries({ queryKey: ["mira-conversations"] });
      onDeleted(id);
    },
  });

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    try {
      return new Intl.DateTimeFormat(i18n.language === "he" ? "he-IL" : "en-US", { dateStyle: "medium" }).format(d);
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-testid="mira-conversations">
      <div className="border-b border-slate-100 px-4 py-2">
        <button
          type="button"
          data-testid="mira-conversation-new"
          onClick={onNew}
          disabled={busy}
          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >{t("miraPanel.newConversation")}</button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {reopenFailed ? (
          <p data-testid="mira-conversation-not-found" className="mb-2 text-sm text-slate-500">{t("miraPanel.conversationNotFound")}</p>
        ) : null}
        {listQuery.isLoading ? (
          <p className="text-sm text-slate-400">{t("miraPanel.loading")}</p>
        ) : rows.length === 0 ? (
          <p data-testid="mira-conversations-empty" className="text-sm text-slate-500">{t("miraPanel.conversationsEmpty")}</p>
        ) : (
          <div className="space-y-1.5">
            {rows.map((row) => {
              const title = displayTitle(row);
              const date = formatDate(row.updated_at || row.created_at);
              const open = () => { if (!busy) onOpen(row.id); };
              return (
                <div
                  key={row.id}
                  data-testid="mira-conversation-row"
                  data-active={row.id === activeId ? "true" : "false"}
                  role="button"
                  tabIndex={0}
                  aria-disabled={busy}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === "Enter") open(); }}
                  className={`group block w-full cursor-pointer rounded-xl border p-3 text-start transition hover:border-gold-soft hover:bg-ivory-2 ${row.id === activeId ? "border-primary/40 bg-primary/5" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {title ? (
                      <span data-testid="mira-conversation-title" className="min-w-0 truncate font-semibold text-slate-900">{title}</span>
                    ) : (
                      <span className="min-w-0 truncate font-semibold text-slate-900">{date}</span>
                    )}
                    <div className="flex flex-none items-center gap-2">
                      {title ? <span data-testid="mira-conversation-date" className="text-[11px] text-slate-400">{date}</span> : null}
                      <button
                        type="button"
                        data-testid="mira-conversation-delete"
                        aria-label={t("miraPanel.deleteAria", { title: title || date })}
                        onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(row.id); }}
                        disabled={deleteMutation.isPending}
                        className="rounded-md p-1 text-slate-400 opacity-60 transition hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// W64 — the line an upload leaves in the thread: uploading, then filed (with the way to My Library),
// or the honest upload error. Never the filename.
function MiraUploadNote({ message }) {
  const { t } = useTranslation();
  return (
    <div data-testid="mira-upload-note" data-status={message.status} className="ms-auto max-w-[85%] text-end text-xs text-slate-500">
      {message.status === "uploading" ? t("planning.uploading") : null}
      {message.status === "done" ? (
        <>
          {t("planning.toastUploaded")}{" "}
          <Link to="/my-plans" data-testid="mira-upload-open-library" className="font-semibold text-primary underline hover:text-primary/80">
            {t("redesign.plans.openMyPlanning")}
          </Link>
        </>
      ) : null}
      {message.status === "error" ? <span className="text-slate-700">{message.errorText}</span> : null}
    </div>
  );
}

function MiraNoticed({ queryClient }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null); // belief id being corrected
  const [draft, setDraft] = useState("");

  const beliefsQuery = useQuery({
    queryKey: ["mira-beliefs"],
    queryFn: () => reflectiveApi.beliefs().then((res) => res.data),
    retry: false,
  });

  const correctionMutation = useMutation({
    mutationFn: (payload) => reflectiveApi.correction(payload).then((res) => res.data),
    onSuccess: () => {
      setEditing(null);
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["mira-beliefs"] });
    },
  });

  const beliefs = beliefsQuery.data?.beliefs || [];

  return (
    <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
      <p className="text-xs leading-5 text-slate-500">{t("miraPanel.noticedIntro")}</p>
      {beliefs.length === 0 ? (
        <p className="text-sm text-slate-500">{t("miraPanel.noticedEmpty")}</p>
      ) : (
        beliefs.map((belief) => (
          <div key={belief.id} data-testid="mira-belief-row" className="rounded-lg border border-slate-200 p-3">
            <div className="text-sm leading-6 text-slate-800">{belief.text}</div>
            <div className="mt-1 flex items-center justify-between">
              <span
                data-testid="mira-belief-provenance"
                className="text-[11px] font-medium uppercase tracking-wide text-slate-400"
              >
                {belief.provenance === "user" ? t("miraPanel.provenanceUser") : t("miraPanel.provenanceInference")}
              </span>
              {editing === belief.id ? null : (
                <button
                  type="button"
                  data-testid="mira-correct-button"
                  onClick={() => { setEditing(belief.id); setDraft(belief.text); }}
                  className="text-xs font-semibold text-primary hover:text-primary/80"
                >{t("miraPanel.correctThis")}</button>
              )}
            </div>
            {editing === belief.id ? (
              <div className="mt-2 space-y-2">
                <input
                  data-testid="mira-correction-input"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="mira-correction-save"
                    onClick={() => correctionMutation.mutate({ belief_id: belief.id, correction: draft.trim() })}
                    disabled={!draft.trim() || correctionMutation.isPending}
                    className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  >{t("miraPanel.save")}</button>
                  <button
                    type="button"
                    onClick={() => { setEditing(null); setDraft(""); }}
                    className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >{t("miraPanel.cancel")}</button>
                </div>
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
}

// P7b — the confirmation card. Mira PROPOSES; the user sees EXACTLY what will happen and clicks
// Confirm (once) or Cancel. Only on Confirm does the backend execute (re-validated, as the user).
// HEB-1: locale-aware, timezone-local human-readable datetime — no raw ISO ever reaches the user.
function formatDateTime(iso, language) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(language === "he" ? "he-IL" : "en-US", { dateStyle: "medium", timeStyle: "short" }).format(d);
  } catch {
    return iso;
  }
}

function ProposalDetail({ action, payload }) {
  const { t, i18n } = useTranslation();
  if (action === "generate_doc") {
    return (
      <>
        <p className="font-semibold text-slate-900">{t("miraPanel.proposalCreateDoc", { title: payload.title })}</p>
        {payload.body ? <p className="mt-1 line-clamp-3 text-xs text-slate-500">{payload.body}</p> : null}
      </>
    );
  }
  if (action === "create_event") {
    const n = (payload.attendee_ids || []).length;
    // en-dash range separator (not a directional arrow): bidi-neutral, so a start–end time range
    // orders correctly under both LTR and RTL paragraph direction without needing to mirror a glyph.
    const when = formatDateTime(payload.start_at, i18n.language)
      + (payload.end_at ? ` – ${formatDateTime(payload.end_at, i18n.language)}` : "");
    return (
      <>
        <p className="font-semibold text-slate-900">{t("miraPanel.proposalSchedule", { title: payload.title })}</p>
        <p className="mt-1 text-xs text-slate-500">
          {when}
          {n ? ` · ${t("miraPanel.attendeeCount", { count: n })}` : ""}
          {payload.recurrence ? ` · ${t("miraPanel.repeats")}` : ""}
          {payload.location ? ` · ${payload.location}` : ""}
        </p>
      </>
    );
  }
  if (action === "send_message") {
    const to = payload.recipient_ids
      ? t("miraPanel.recipientCount", { count: payload.recipient_ids.length })
      : t("miraPanel.targetConversation");
    return (
      <>
        <p className="font-semibold text-slate-900">{t("miraPanel.proposalSendTo", { target: to })}</p>
        {/* The FULL text the user is about to send, verbatim. */}
        <p className="mt-1 rounded-md bg-white px-2 py-1.5 text-sm text-slate-700">{payload.text}</p>
      </>
    );
  }
  return <p className="font-semibold text-slate-900">{t("miraPanel.proposalGeneric")}</p>;
}

// Cluster B — the document view: a reading surface for Mira's created documents. List (newest
// first) → reader (title + legible body). The body is plain text from Mira; whitespace is
// preserved (no markdown dependency added).
function MiraDocuments({ activeDocId, setActiveDocId }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const listQuery = useQuery({
    queryKey: ["mira-documents"],
    queryFn: () => reflectiveApi.documents().then((res) => res.data),
    retry: false,
  });
  const docs = listQuery.data?.documents || [];

  const invalidateDocs = () => queryClient.invalidateQueries({ queryKey: ["mira-documents"] });

  const restoreMutation = useMutation({
    mutationFn: (id) => reflectiveApi.restoreDocument(id).then((res) => res.data),
    onSuccess: invalidateDocs,
  });
  const deleteMutation = useMutation({
    // SOFT-delete: the doc is set deleted=true (list refetch drops it). Undo restores it; after 7s
    // the toast dismisses and the soft-delete stands. Soft-delete keeps undo always correct.
    mutationFn: (id) => reflectiveApi.deleteDocument(id).then((res) => res.data),
    onSuccess: (_data, id) => {
      invalidateDocs();
      toast(t("miraPanel.docDeletedTitle"), {
        description: t("miraPanel.docDeletedBody"),
        duration: 7000,
        action: { label: t("miraPanel.undo"), onClick: () => restoreMutation.mutate(id) },
      });
    },
  });

  const handleDelete = (event, id) => {
    event.stopPropagation();          // don't open the reader when clicking the trash
    if (activeDocId === id) setActiveDocId(null);  // if reading it, drop back to the list
    deleteMutation.mutate(id);
  };

  const docQuery = useQuery({
    queryKey: ["mira-document", activeDocId],
    queryFn: () => reflectiveApi.document(activeDocId).then((res) => res.data),
    enabled: Boolean(activeDocId),
    retry: false,
  });

  // MIRA-2 Unit L — in-place edit (title + body) and .md download.
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const editMutation = useMutation({
    mutationFn: ({ id, title, body }) => reflectiveApi.editDocument(id, { title, body }).then((res) => res.data),
    onSuccess: () => {
      invalidateDocs();
      queryClient.invalidateQueries({ queryKey: ["mira-document", activeDocId] });
      setEditing(false);
    },
  });
  const startEdit = (doc) => { setEditTitle(doc.title || ""); setEditBody(doc.body || ""); setEditing(true); };
  const saveEdit = () => {
    const title = editTitle.trim();
    const body = editBody.trim();
    if (!title || !body) return;
    editMutation.mutate({ id: activeDocId, title, body });
  };
  const downloadMarkdown = (doc) => {
    // Client-side export — the honest, cheapest format (docx is out of scope / PLAN-3-adjacent).
    try {
      if (typeof URL === "undefined" || !URL.createObjectURL) return;
      const md = `# ${doc.title || ""}\n\n${doc.body || ""}\n`;
      const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc.title || "document").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || "document"}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* download is best-effort — never break the reader */
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  // Reader
  if (activeDocId) {
    const doc = docQuery.data;
    return (
      <div className="flex flex-1 flex-col overflow-hidden" data-testid="mira-document-reader">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
          <button type="button" data-testid="mira-document-back" onClick={() => { setEditing(false); setActiveDocId(null); }} className="text-xs font-medium text-primary hover:text-primary/80">
            <span aria-hidden="true" className="rtl:inline-block rtl:-scale-x-100">←</span> {t("miraPanel.allDocuments")}
          </button>
          {doc && !editing ? (
            <div className="flex flex-none items-center gap-3">
              <button type="button" data-testid="mira-document-edit" onClick={() => startEdit(doc)} className="text-xs font-medium text-slate-600 hover:text-slate-900">{t("miraPanel.editDoc")}</button>
              <button type="button" data-testid="mira-document-download" onClick={() => downloadMarkdown(doc)} className="text-xs font-medium text-slate-600 hover:text-slate-900">{t("miraPanel.downloadDoc")}</button>
            </div>
          ) : null}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {docQuery.isLoading ? (
            <p className="text-sm text-slate-400">{t("miraPanel.opening")}</p>
          ) : doc ? (
            editing ? (
              <div className="flex flex-col gap-2" data-testid="mira-document-editor">
                <input
                  data-testid="mira-edit-title"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="rounded-md border border-slate-200 px-3 py-2 font-serif text-lg font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                />
                <textarea
                  data-testid="mira-edit-body"
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={12}
                  className="rounded-md border border-slate-200 px-3 py-2 text-sm leading-7 text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                />
                <div className="flex items-center gap-2">
                  <button type="button" data-testid="mira-edit-save" onClick={saveEdit} disabled={editMutation.isPending} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50">{t("miraPanel.save")}</button>
                  <button type="button" data-testid="mira-edit-cancel" onClick={() => setEditing(false)} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">{t("miraPanel.cancel")}</button>
                </div>
              </div>
            ) : (
              <article>
                <h2 className="font-serif text-xl font-semibold leading-snug text-slate-900">{doc.title}</h2>
                <p className="mt-1 text-xs text-slate-400">{formatDate(doc.created_at)}</p>
                <div className="mt-4 whitespace-pre-wrap text-sm leading-7 text-slate-700">{doc.body}</div>
              </article>
            )
          ) : (
            <p className="text-sm text-slate-500">{t("miraPanel.docNotFound")}</p>
          )}
        </div>
      </div>
    );
  }

  // List
  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-testid="mira-documents">
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {listQuery.isLoading ? (
          <p className="text-sm text-slate-400">{t("miraPanel.loading")}</p>
        ) : docs.length === 0 ? (
          <div data-testid="mira-documents-empty" className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 text-center">
            <p className="text-sm font-medium text-slate-700">{t("miraPanel.docsEmptyTitle")}</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">{t("miraPanel.docsEmptyBody")}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {docs.map((d) => (
              <div
                key={d.id}
                data-testid="mira-document-row"
                role="button"
                tabIndex={0}
                onClick={() => setActiveDocId(d.id)}
                onKeyDown={(e) => { if (e.key === "Enter") setActiveDocId(d.id); }}
                className="group block w-full cursor-pointer rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-gold-soft hover:bg-ivory-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate font-semibold text-slate-900">{d.title}</span>
                  <div className="flex flex-none items-center gap-2">
                    <span className="text-[11px] text-slate-400">{formatDate(d.created_at)}</span>
                    <button
                      type="button"
                      data-testid="mira-document-delete"
                      aria-label={t("miraPanel.deleteAria", { title: d.title })}
                      onClick={(e) => handleDelete(e, d.id)}
                      className="rounded-md p-1 text-slate-400 opacity-60 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {d.snippet ? <p className="mt-0.5 truncate text-xs text-slate-500">{d.snippet}</p> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiraProposalCard({ message, onResolve, onOpenDocument, onDocumentCreated }) {
  const { t } = useTranslation();
  const { proposal, status, result } = message;
  const queryClient = useQueryClient();
  const executeMutation = useMutation({
    mutationFn: () =>
      reflectiveApi
        .executeAction({
          action: proposal.action,
          payload: proposal.payload,
          // SIG-2: echo the original proposed payload (confirmed-vs-modified classification) and the
          // suggestion_key when this proposal came from a proactive suggestion (suggestion_acted).
          proposed: proposal.payload,
          suggestion_key: message.suggestion_key,
        })
        .then((res) => res.data),
    onSuccess: (data) => {
      // Refresh gap: Mira runs in her own launcher, so a separate calendar/chat surface — and the
      // Documents tab — won't see the new artifact until its own poll. Invalidate the shared query
      // so an already-open surface refetches immediately.
      const type = data?.result?.type;
      if (type === "event") queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      if (type === "message") queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      if (type === "document") {
        queryClient.invalidateQueries({ queryKey: ["mira-documents"] });
        // MIRA-2 Unit L: a PERSISTENT creation toast with a deep-link — the in-card link scrolls
        // away, so a tester who dismisses it can still reopen the doc. Also flag the Documents tab
        // with a "new" dot so the artifact is discoverable even if the toast is missed.
        if (data?.result?.id) {
          toast(t("miraPanel.docCreatedTitle"), {
            description: t("miraPanel.docCreatedBody"),
            duration: 8000,
            action: { label: t("miraPanel.openDocument"), onClick: () => onOpenDocument && onOpenDocument(data.result.id) },
          });
          if (onDocumentCreated) onDocumentCreated(data.result.id);
        }
      }
      onResolve({ status: "done", result: data?.result });
    },
    onError: () => onResolve({ status: "error" }),
  });

  const RESULT_TEXT = { document: t("miraPanel.resultDocument"), event: t("miraPanel.resultEvent"), message: t("miraPanel.resultMessage") };

  return (
    <div data-testid="mira-proposal" data-action={proposal.action} className="rounded-2xl border border-gold-soft bg-ivory-2 p-3">
      <ProposalDetail action={proposal.action} payload={proposal.payload} />

      {status === "pending" ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="mira-proposal-confirm"
            onClick={() => executeMutation.mutate()}
            disabled={executeMutation.isPending}
            className="rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {executeMutation.isPending ? t("miraPanel.working") : t("miraPanel.confirm")}
          </button>
          <button
            type="button"
            data-testid="mira-proposal-cancel"
            onClick={() => {
              // SIG-2: a declined proposal is the strongest training signal — fire-and-forget,
              // never block or toast on a capture failure.
              reflectiveApi
                .rejectAction({ action: proposal.action, payload: proposal.payload, summary: proposal.summary })
                .catch(() => {});
              onResolve({ status: "cancelled" });
            }}
            className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-white"
          >{t("miraPanel.cancel")}</button>
        </div>
      ) : null}

      {status === "done" ? (
        <p data-testid="mira-proposal-result" className="mt-2 text-sm font-medium text-emerald-700">
          {RESULT_TEXT[result?.type] || t("miraPanel.resultDone")}
          {/* Cluster B: a created document now has somewhere to live — link straight to it. */}
          {result?.type === "document" && result?.id ? (
            <button
              type="button"
              data-testid="mira-proposal-open-doc"
              onClick={() => onOpenDocument && onOpenDocument(result.id)}
              className="ms-2 font-semibold text-primary underline hover:text-primary/80"
            >{t("miraPanel.openDocument")}</button>
          ) : null}
        </p>
      ) : null}
      {status === "cancelled" ? (
        <p data-testid="mira-proposal-cancelled" className="mt-2 text-sm text-slate-500">{t("miraPanel.cancelled")}</p>
      ) : null}
      {status === "error" ? (
        <p data-testid="mira-proposal-error" className="mt-2 text-sm text-rose-600">{t("miraPanel.actionError")}</p>
      ) : null}
    </div>
  );
}

// SIG-2 — the thumbs pair on a Mira response. Small and unobtrusive; selected-state highlights the
// last click (latest-wins, no un-rate in this pass). Localized aria/tooltips (mira.rateUp/rateDown).
function MiraResponseRating({ messageId, value, onRate }) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex gap-1" data-testid="mira-rating">
      <button
        type="button"
        data-testid="mira-rate-up"
        aria-pressed={value === "up"}
        aria-label={t("mira.rateUp")}
        title={t("mira.rateUp")}
        onClick={() => onRate(messageId, "up")}
        className={`rounded p-1 transition ${value === "up" ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}`}
      >
        <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        data-testid="mira-rate-down"
        aria-pressed={value === "down"}
        aria-label={t("mira.rateDown")}
        title={t("mira.rateDown")}
        onClick={() => onRate(messageId, "down")}
        className={`rounded p-1 transition ${value === "down" ? "text-rose-600" : "text-slate-400 hover:text-slate-600"}`}
      >
        <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

export default MiraLauncher;